import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  CalendarDays, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  Clock, 
  XCircle,
  FileText,
  Scale,
  ClipboardCheck,
  Users
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { API_URL } from "../lib/utils";

// Ícone do Google
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState("");
  
  // Verificar se há erro passado via state (ex: de AuthCallback)
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      setErrorType("");
      // Limpar o state para não mostrar o erro novamente
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Estatísticas para prova social
  const [stats, setStats] = useState({
    documentos_monitorados: 70,
    itens_calibraveis: 82,
    verificacoes_executadas: 350,
    funcionarios_monitorados: 18
  });

  // Buscar estatísticas públicas
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Erro ao buscar estatísticas:", error);
      }
    };
    fetchStats();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorType("");
    setLoading(true);

    try {
      const response = await login(email, senha);
      if (response.deve_trocar_senha) {
        navigate("/trocar-senha");
      } else if (response.user?.is_super_admin) {
        navigate("/super-admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("aguardando aprovacao")) {
        setErrorType("pendente");
        setError("Seu cadastro ainda esta aguardando aprovacao. Por favor, aguarde a liberacao.");
      } else if (msg.includes("rejeitado")) {
        setErrorType("rejeitado");
        setError("Seu cadastro foi rejeitado. Entre em contato com o suporte para mais informações.");
      } else {
        setErrorType("");
        setError(msg || "Erro ao fazer login. Verifique suas credenciais.");
      }
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // Usar window.location.origin para obter o domínio atual dinamicamente
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Componente para os cards de estatísticas
  const StatCard = ({ icon: Icon, value, label, color }) => (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all duration-300">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">+{value.toLocaleString('pt-BR')}</div>
      <div className="text-teal-100 text-sm">{label}</div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Lado Esquerdo - Logo e Formulário */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 bg-white dark:bg-slate-900">
        <div className="w-full max-w-md">
          {/* Logomarca */}
          <div className="flex flex-col items-center mb-8">
            <picture>
              <source srcSet="/images/logomarca.webp" type="image/webp" />
              <img 
                src="/images/logomarca.png" 
                alt="Conforme a Resolução" 
                className="w-40 h-auto mb-4"
                loading="eager"
                decoding="async"
                width="160"
                height="160"
              />
            </picture>
            <h1 className="text-2xl font-bold text-slate-900 text-center" style={{ fontFamily: 'Manrope' }}>
              Conforme a Resolução
            </h1>
            <p className="text-sm text-slate-500">Gestão Documental para Farmácias</p>
          </div>

          {/* Card de Login */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Bem-vindo de volta</CardTitle>
              <CardDescription>Entre com suas credenciais para acessar</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && errorType === "pendente" && (
                  <Alert className="bg-amber-50 border-amber-200" data-testid="login-pending">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">{error}</AlertDescription>
                  </Alert>
                )}
                
                {error && errorType === "rejeitado" && (
                  <Alert className="bg-red-50 border-red-200" data-testid="login-rejected">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">{error}</AlertDescription>
                  </Alert>
                )}
                
                {error && !errorType && (
                  <Alert variant="destructive" data-testid="login-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="senha">Senha</Label>
                    <Link 
                      to="/esqueci-senha" 
                      className="text-xs text-teal-600 hover:text-teal-700"
                      data-testid="forgot-password-link"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="senha"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={loading || googleLoading}
                  data-testid="login-submit"
                >
                  {loading ? "Entrando..." : "Entrar"}
                </Button>
              </form>

              {/* Divisor */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-slate-500">ou continue com</span>
                </div>
              </div>

              {/* Botão Google */}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={loading || googleLoading}
                data-testid="google-login-btn"
              >
                {googleLoading ? (
                  <div className="w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <GoogleIcon />
                )}
                <span className="ml-2">
                  {googleLoading ? "Redirecionando..." : "Continuar com Google"}
                </span>
              </Button>

              <p className="text-center text-sm text-slate-500 mt-6">
                Não tem uma conta?{" "}
                <Link to="/registro" className="text-teal-600 hover:text-teal-700 font-medium">
                  Criar conta
                </Link>
              </p>
              
              <p className="text-center text-xs text-slate-400 mt-4">
                Ao entrar, você concorda com nossos{" "}
                <Link to="/termos" className="text-teal-600 hover:underline">
                  Termos de Uso e Política de Privacidade
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lado Direito - Prova Social com Estatísticas */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-teal-600 to-teal-700 items-center justify-center p-12">
        <div className="max-w-lg w-full">
          {/* Título da seção */}
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>
              Resultados que Falam
            </h2>
            <p className="text-teal-100 text-lg">
              Farmácias em todo o Brasil confiam no Conforme a Resolução para manter sua documentação em dia.
            </p>
          </div>

          {/* Grid de Estatísticas */}
          <div className="grid grid-cols-2 gap-4">
            <StatCard 
              icon={FileText}
              value={stats.documentos_monitorados}
              label="Documentos e alvarás monitorados"
              color="bg-white/20"
            />
            <StatCard 
              icon={Scale}
              value={stats.itens_calibraveis}
              label="Itens calibráveis cadastrados"
              color="bg-white/20"
            />
            <StatCard 
              icon={ClipboardCheck}
              value={stats.verificacoes_executadas}
              label="Verificações executadas"
              color="bg-white/20"
            />
            <StatCard 
              icon={Users}
              value={stats.funcionarios_monitorados}
              label="Funcionários monitorados"
              color="bg-white/20"
            />
          </div>

          {/* Texto adicional */}
          <div className="mt-8 text-center">
            <p className="text-teal-100 text-sm">
              Junte-se a centenas de farmácias que já simplificaram sua gestão documental
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
